import {computed, inject, Injectable, signal} from '@angular/core';
import {
  CreateFeatureRequest,
  FeatureDetails,
  FeatureService,
  FeatureSummary,
  UpdateFeatureRequest,
} from '../../../../api';
import {lastValueFrom} from 'rxjs';
import {injectMutation, injectQuery, QueryClient} from '@tanstack/angular-query-experimental';
import {QueryKey} from '../../../constants/query-key';

@Injectable()
export class FeatureStoreService {
  private readonly featureService = inject(FeatureService);
  private readonly queryClient = inject(QueryClient);

  selectedFeatureId = signal<number | null>(null);
  searchTerm = signal<string | null>(null);

  private readonly featuresQuery = injectQuery(() => ({
    queryKey: [QueryKey.FEATURES, this.searchTerm()],
    queryFn: () => lastValueFrom(this.featureService.getFeatures(this.searchTerm() ?? '')),
  }));

  features = computed<FeatureSummary[]>(() => this.featuresQuery.data()?.features ?? []);
  isLoading = computed(() => this.featuresQuery.isLoading());

  selectedFeatureQuery = injectQuery(() => ({
    queryKey: [QueryKey.FEATURE, this.selectedFeatureId()],
    queryFn: () => lastValueFrom(this.featureService.getFeatureById(this.selectedFeatureId()!)),
    enabled: this.selectedFeatureId() !== null,
    staleTime: 0,
  }));
  selectedFeature = computed<FeatureDetails | null>(() => {
    if (this.selectedFeatureId() === null) return null;
    return this.selectedFeatureQuery.data()?.feature ?? null;
  });
  isLoadingSelected = computed(() => this.selectedFeatureQuery.isLoading());

  createFeatureMutation = injectMutation(() => ({
    mutationFn: (request: CreateFeatureRequest) =>
      lastValueFrom(this.featureService.createFeature(request)),
    onSuccess: () => {
      this.queryClient.invalidateQueries({queryKey: [QueryKey.FEATURES]});
      this.queryClient.invalidateQueries({queryKey: [QueryKey.FEATURE_SELECT]});
    },
  }));

  updateFeatureMutation = injectMutation(() => ({
    mutationFn: (request: { id: number; body: UpdateFeatureRequest }) =>
      lastValueFrom(this.featureService.updateFeature(request.id, request.body)),
    onSuccess: (_, request) => {
      this.queryClient.invalidateQueries({queryKey: [QueryKey.FEATURES]});
      this.queryClient.invalidateQueries({queryKey: [QueryKey.FEATURE, request.id]});
      this.queryClient.invalidateQueries({queryKey: [QueryKey.FEATURE_SELECT]});
    },
  }));

  deleteFeatureMutation = injectMutation(() => ({
    mutationFn: (id: number) => lastValueFrom(this.featureService.deleteFeature(id)),
    onSuccess: () => {
      this.queryClient.invalidateQueries({queryKey: [QueryKey.FEATURES]});
      this.queryClient.invalidateQueries({queryKey: [QueryKey.FEATURE_SELECT]});
    },
  }));
}
